import { addMinutes } from "date-fns";
import type { FieldPath, FieldValues, UseControllerProps } from "react-hook-form";
import { useController } from "react-hook-form";

import type { DatePickerProps } from "@/components/DatePicker/DatePicker";
import { DatePicker } from "@/components/DatePicker/DatePicker";
import { FormattedDate } from "@/components/FormattedDate/FormattedDate";
import { Button } from "@/design-system/Button";
import { Inline } from "@/design-system/Inline";
import { Stack } from "@/design-system/Stack";
import { useBool } from "@/hooks/useBool";
import { useTranslation } from "@/translations";

import { IconButton } from "../Button/IconButton";
import { Trash01Icon } from "../Icons/Trash01Icon";
import { FormErrorWrapper } from "./FormErrorWrapper";

type Props<
  TFormValues extends FieldValues,
  TName extends FieldPath<TFormValues> = FieldPath<TFormValues>,
> = UseControllerProps<TFormValues, TName> &
  Pick<DatePickerProps, "min" | "max"> & {
    labelWhenNoValue?: string;
    canEdit: boolean;
    onChange?: (value: Date | undefined) => void;
  };
export function FormScheduleInput<
  TFormValues extends FieldValues,
  TName extends FieldPath<TFormValues> = FieldPath<TFormValues>,
>({
  name,
  control,
  rules,
  shouldUnregister,
  defaultValue,
  onChange,
  canEdit,
  labelWhenNoValue,
  ...props
}: Props<TFormValues, TName>): React.ReactNode {
  const { t } = useTranslation();
  const [isScheduleOpen, scheduleHandler] = useBool();
  const {
    field: { onChange: fieldOnChange, ...field },
    fieldState,
  } = useController<TFormValues, TName>({
    name,
    control,
    rules,
    shouldUnregister,
    defaultValue,
  });

  const handleDiscard = () => {
    fieldOnChange(undefined);
    scheduleHandler.setFalse();
  };

  const handleEdit = () => {
    if (!field.value) {
      fieldOnChange(defaultValue || props.min || addMinutes(new Date(), 5));
    }

    scheduleHandler.setTrue();
  };

  return (
    <FormErrorWrapper name={name}>
      <Stack align="start" gap="sm">
        <Inline align="center" gap="sm" wrap={false}>
          {field.value && (
            <span className="flex flex-wrap gap-x-1">
              {isScheduleOpen ? (
                t("component.schedule-input.state.scheduled")
              ) : (
                <FormattedDate date={field.value} format="datetime" />
              )}
            </span>
          )}
          {!field.value && (
            <span className="flex flex-wrap gap-x-1 text-body">
              {labelWhenNoValue || t("component.schedule-input.state.immediate")}
            </span>
          )}
          {!isScheduleOpen && (
            <Button variant="ghostPrimary" data-testid="schedule-btn" onClick={handleEdit} disabled={!canEdit}>
              {t("common.action.edit")}
            </Button>
          )}
        </Inline>
        {isScheduleOpen && (
          <Inline gap="sm" wrap={false}>
            <DatePicker
              data-testid="scheduled-date-time"
              {...field}
              {...props}
              type="datetime"
              onChange={(date: Date | undefined) => {
                fieldOnChange(date);
                onChange?.(date);
              }}
              error={fieldState.error != null}
              aria-invalid={fieldState.error != null}
            />
            <IconButton
              icon={<Trash01Icon />}
              onClick={handleDiscard}
              variant="filled"
              tone="error"
              title={t("common.action.delete")}
            />
          </Inline>
        )}
      </Stack>
    </FormErrorWrapper>
  );
}
